import { useToggle } from "hooks"
import { useTranslation } from "react-i18next"
import { addPlayLinkParams } from "../../../tools/addPlayLinkParams"
import { getAttensiProductPlatformForOS } from "../../../tools/getAttensiProductPlatformForOS"
import { IS_APP } from "../../../vuplex/constants"
import { LauncherOverlay, NotSupported } from "../components"
import { HowToPlay } from "../ProductModalHelp"
import { ProductModalMain } from "../ProductModalMain"
import type { Data } from "./gql"

// Real Talk product can be displayed either in the Launcher, similarly
// to other app based products, or in a browser - but it uses a stream link
// instead of WebGL like other browser based products. This makes it a bit
// different so it has its own set of buttons.
//
// On mobile devices Real Talk is only supported as the web app (webglLink).
//
// TODO [api-decoupling] Replace with type not tied to Data
type Props = {
  productType: string
  playLinks: Data["playLinks"]
  useTransferToken: Data["useTransferToken"]
  supportedPlatforms: Data["supportedPlatforms"]
}
export const RealTalkProductButtons = ({
  productType,
  playLinks,
  useTransferToken,
  supportedPlatforms,
}: Props) => {
  const [isLauncherOverlayVisible, toggleLauncherOverlay] = useToggle()

  const { launcherLink, streamLink, webglLink } = playLinks
  const hasMultiplePlayLinks =
    [launcherLink, streamLink, webglLink].filter(Boolean).length > 1

  const onLauncherButtonClick = async () => {
    toggleLauncherOverlay()
    const link = await addPlayLinkParams({
      link: launcherLink,
      useTransferToken,
    })
    if (link) window.location.href = link
  }

  // The launcher and the ShortsWeb stream have no mobile UI, so only the web
  // app (webglLink) is offered on mobile / inside Skills+. RealTalkViewer
  // repeats this check so the buttons and the viewer agree on "mobile".
  const platform = getAttensiProductPlatformForOS()
  const mobile = IS_APP || platform === "iOS" || platform === "Android"
  // "real_talk_maker" and "skills_real_talk" don't have a proper launcher link.
  const canUseLauncher =
    Boolean(launcherLink) && productType === "real_talk" && !mobile
  const canUseStream = Boolean(streamLink) && !mobile
  const canUseWebgl = Boolean(webglLink)
  const isPlayable = canUseLauncher || canUseStream || canUseWebgl

  const shouldShowLauncherLink = canUseLauncher
  const shouldShowPlayInBrowser = canUseWebgl || canUseStream

  if (mobile && !canUseWebgl) return <NotSupported.Mobile />
  if (!isPlayable)
    return <NotSupported.Platform supportedPlatforms={supportedPlatforms} />

  return (
    <>
      {shouldShowPlayInBrowser && (
        <PlayInBrowserButton isPrimary={!hasMultiplePlayLinks} />
      )}

      {shouldShowLauncherLink && (
        <LauncherButton
          isPrimary={!hasMultiplePlayLinks}
          onClick={onLauncherButtonClick}
        />
      )}

      {hasMultiplePlayLinks && (
        <HowToPlay
          playType={[
            "launcher",
            ...(streamLink ? ["stream" as const] : []),
            ...(webglLink ? ["webgl" as const] : []),
          ]}
        />
      )}

      {isLauncherOverlayVisible && (
        <LauncherOverlay onBack={toggleLauncherOverlay} />
      )}
    </>
  )
}

type PlayInBrowserButtonProps = { isPrimary: boolean }
const PlayInBrowserButton = ({ isPrimary }: PlayInBrowserButtonProps) => {
  const { t } = useTranslation()
  return (
    <ProductModalMain.PlayLink to="play">
      {isPrimary
        ? t("product.PLAY_TEXT_SIM")
        : t("content.PRODUCT_WEBGL_LAUNCHER")}
    </ProductModalMain.PlayLink>
  )
}

type LauncherButtonProps = {
  isPrimary: boolean
  onClick: () => void
}
const LauncherButton = ({ isPrimary, onClick }: LauncherButtonProps) => {
  const { t } = useTranslation()

  return (
    <ProductModalMain.PlayLink
      onClick={onClick}
      linkVariant={isPrimary ? "theme" : "ghost"}
    >
      {isPrimary
        ? t("product.PLAY_TEXT_SIM")
        : t("content.PRODUCT_DESKTOP_LAUNCHER")}
    </ProductModalMain.PlayLink>
  )
}
